/**
 * Buyer (заказчик) name autocomplete client — GET /buyers/autocomplete.
 *
 * Backs the catalog `customer` filter and the saved-search builder's buyer
 * field (Task 8): the user types a buyer name, this resolves it to a TIN
 * (`Tender.customerTin` / `SavedSearch.customerTin`), which is what actually
 * filters. Mirrors the fetch conventions the rest of this module's siblings
 * use (`catalog-client.ts`'s `searchIkpu`) — `apiRequest`, not a bespoke
 * `fetch`/base-URL, so it inherits the session cookie, the bearer token and
 * the `{ data }` envelope unwrap for free.
 */
import { apiRequest } from './api';

export interface BuyerHit {
  tin: string;
  name: string;
  tenderCount: number;
}

/**
 * Below 2 characters the endpoint does a broad ILIKE scan and would fan out
 * to hundreds of buyers for no useful narrowing — same floor `searchIkpu`-
 * style pickers use elsewhere in this app. Returns `[]` rather than calling
 * the API at all, so a fresh keystroke never fires a request that would just
 * be discarded.
 */
const MIN_QUERY_LENGTH = 2;

/**
 * Resolve a partial buyer name to matching `{ tin, name, tenderCount }` hits.
 *
 * Degrades to `[]` on a network/API failure rather than throwing — this is a
 * type-ahead suggestion list, not a page-critical fetch, and a failed request
 * should read as "no matches yet", not crash the picker mid-keystroke.
 */
export async function searchBuyers(q: string, signal?: AbortSignal): Promise<BuyerHit[]> {
  const term = q.trim();
  if (term.length < MIN_QUERY_LENGTH) return [];
  try {
    const hits = await apiRequest<BuyerHit[]>(
      `/buyers/autocomplete?q=${encodeURIComponent(term)}`,
      { signal },
    );
    return hits ?? [];
  } catch {
    return [];
  }
}
